import { useId, useState, type ReactNode } from 'react';
import { ChevronDown, Eye, EyeOff, Pencil, Plus, RotateCcw, Search, Trash2 } from 'lucide-react';
import {
  BoutonPrincipal,
  BoutonSecondaire,
  Chargement,
  EnteteEcran,
  MessageErreur,
  Pastille,
} from '../components/ui/Primitives';
import { ChampFiltre } from '../components/ui/Onglets';
import { Modale } from '../components/ui/Modale';
import { useToast } from '../components/ui/useToast';
import { FormulaireArticle } from '../components/faq/FormulaireArticle';
import { ErreurApi } from '../lib/api';
import { useValeurDifferee } from '../lib/useRecherche';
import { jourCourt } from '../lib/useFormateurs';
import { jourCivilReunion } from '../lib/dates-civiles';
import type { Profil } from '../lib/types';
import {
  useFaq,
  usePublierArticleFaq,
  useSupprimerArticleFaq,
  type ArticleFaq,
  type FiltresFaq,
  type GestionFaq,
  type RubriqueFaq,
} from '../lib/useFaq';

function messageErreur(erreur: unknown, defaut: string): string {
  return erreur instanceof ErreurApi ? erreur.message : defaut;
}

const SANS_FILTRE: FiltresFaq = { recherche: '', rubrique: '', vuPar: '' };

/** Articles consécutifs d'une même rubrique : l'API les rend dans l'ordre des rubriques. */
function parRubrique(articles: ArticleFaq[]): { code: RubriqueFaq; libelle: string; articles: ArticleFaq[] }[] {
  const groupes: { code: RubriqueFaq; libelle: string; articles: ArticleFaq[] }[] = [];
  for (const article of articles) {
    const dernier = groupes[groupes.length - 1];
    if (dernier?.code === article.rubrique) dernier.articles.push(article);
    else groupes.push({ code: article.rubrique, libelle: article.libelleRubrique, articles: [article] });
  }
  return groupes;
}

/**
 * Aide & FAQ (TF-483), écran `help` commun à tous les profils : les questions
 * fréquentes sur la formation et le fonctionnement de la plateforme, avec une
 * recherche et un filtre par rubrique. Chacun lit les articles publiés qui
 * visent son profil.
 *
 * La direction tient la FAQ au même endroit : elle crée, modifie, publie,
 * retire et supprime les articles, et peut lire la FAQ comme un profil la lit
 * (« Vu par »). Ce que chacun voit, la recherche et les comptes par rubrique
 * viennent de `/api/faq` : l'écran affiche.
 */
export function AidePage(): JSX.Element {
  const [filtres, setFiltres] = useState<FiltresFaq>(SANS_FILTRE);
  const recherche = useValeurDifferee(filtres.recherche.trim(), 300);
  const { data, isLoading, error } = useFaq({ ...filtres, recherche });
  const [edition, setEdition] = useState<ArticleFaq | 'NOUVEAU' | null>(null);
  const [aSupprimer, setASupprimer] = useState<ArticleFaq | null>(null);
  const idRecherche = useId();

  const changer = <C extends keyof FiltresFaq>(cle: C, valeur: FiltresFaq[C]): void =>
    setFiltres((avant) => ({ ...avant, [cle]: valeur }));
  const gestion = data?.gestion ?? null;
  const filtreActif = filtres.recherche.trim() !== '' || filtres.rubrique !== '';
  const articles = data?.articles ?? [];
  const enRecherche = recherche !== '';

  return (
    <div className="mx-auto max-w-[1100px] space-y-6 p-4 sm:p-6">
      <EnteteEcran
        titre="Aide & FAQ"
        sousTitre="Les réponses aux questions fréquentes sur votre formation et le fonctionnement de la plateforme"
        actions={
          gestion && (
            <BoutonPrincipal onClick={() => setEdition('NOUVEAU')}>
              <Plus className="mr-1.5 h-4 w-4" aria-hidden />
              Nouvel article
            </BoutonPrincipal>
          )
        }
      />

      <div className="space-y-3 rounded-2xl bg-white p-3 shadow-card sm:p-4">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <div className="relative min-w-0 flex-1">
            <label htmlFor={idRecherche} className="sr-only">
              Rechercher dans la FAQ
            </label>
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-codialis-gray" aria-hidden />
            <input
              id={idRecherche}
              type="search"
              value={filtres.recherche}
              onChange={(evenement) => changer('recherche', evenement.target.value)}
              maxLength={gestion?.limites.recherche ?? 200}
              placeholder="Rechercher : émarger, devoir, mot de passe…"
              className="min-h-[44px] w-full rounded-xl border border-gray-200 bg-gray-50 py-2 pl-9 pr-3 text-sm focus:outline-none focus:ring-2 focus:ring-codialis-mint"
            />
          </div>
          {gestion && (
            <ChampFiltre valeur={filtres.vuPar} onChange={(valeur) => changer('vuPar', valeur as Profil | '')} etiquette="Vu par">
              <option value="">Toute la FAQ, brouillons compris</option>
              {gestion.profils.map((profil) => (
                <option key={profil.code} value={profil.code}>
                  Vu par : {profil.libelle}
                </option>
              ))}
            </ChampFiltre>
          )}
        </div>

        {data && data.rubriques.length > 0 && (
          <div role="group" aria-label="Rubriques" className="flex flex-wrap gap-2">
            <BoutonRubrique actif={filtres.rubrique === ''} onClick={() => changer('rubrique', '')}>
              Toutes
            </BoutonRubrique>
            {data.rubriques.map((rubrique) => (
              <BoutonRubrique
                key={rubrique.code}
                actif={filtres.rubrique === rubrique.code}
                onClick={() => changer('rubrique', filtres.rubrique === rubrique.code ? '' : rubrique.code)}
              >
                {rubrique.libelle} <span className="opacity-70">({rubrique.articles})</span>
              </BoutonRubrique>
            ))}
          </div>
        )}
      </div>

      {gestion && <BandeauDirection gestion={gestion} />}

      {isLoading && <Chargement libelle="Chargement de la FAQ…" />}
      {error && <MessageErreur message={messageErreur(error, 'La FAQ n’a pas pu être chargée.')} />}

      {data && !error && articles.length === 0 && (
        <div className="space-y-3 rounded-2xl bg-white px-4 py-12 text-center text-sm text-codialis-gray shadow-card">
          <p>
            {data.total === 0
              ? gestion && !gestion.vuPar
                ? 'La FAQ est vide : créez son premier article.'
                : 'Aucun article n’est encore publié pour ce profil.'
              : 'Aucun article ne correspond à votre recherche.'}
          </p>
          {filtreActif && data.total > 0 && (
            <button
              type="button"
              onClick={() => setFiltres((avant) => ({ ...SANS_FILTRE, vuPar: avant.vuPar }))}
              className="inline-flex min-h-[44px] items-center gap-1.5 rounded-lg px-3 text-xs font-semibold text-codialis-sky hover:underline"
            >
              <RotateCcw className="h-3.5 w-3.5" aria-hidden />
              Effacer la recherche
            </button>
          )}
        </div>
      )}

      {articles.length > 0 && (
        <div className="space-y-6">
          {enRecherche ? (
            <SectionArticles
              titre={`${articles.length} résultat${articles.length > 1 ? 's' : ''}`}
              articles={articles}
              onModifier={setEdition}
              onSupprimer={setASupprimer}
            />
          ) : (
            parRubrique(articles).map((groupe) => (
              <SectionArticles
                key={groupe.code}
                titre={groupe.libelle}
                articles={groupe.articles}
                onModifier={setEdition}
                onSupprimer={setASupprimer}
              />
            ))
          )}
        </div>
      )}

      {gestion && edition && (
        <FormulaireArticle
          gestion={gestion}
          article={edition === 'NOUVEAU' ? null : edition}
          onFermer={() => setEdition(null)}
        />
      )}
      {aSupprimer && <ConfirmationSuppression article={aSupprimer} onFermer={() => setASupprimer(null)} />}
    </div>
  );
}

function BoutonRubrique({ actif, onClick, children }: { actif: boolean; onClick: () => void; children: ReactNode }): JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={actif}
      onClick={onClick}
      className={`min-h-[36px] rounded-full border px-3 text-xs font-semibold transition [@media(pointer:coarse)]:min-h-[40px] ${
        actif ? 'border-codialis-navy bg-codialis-navy text-white' : 'border-gray-200 bg-white text-codialis-navy hover:bg-gray-50'
      }`}
    >
      {children}
    </button>
  );
}

function BandeauDirection({ gestion }: { gestion: GestionFaq }): JSX.Element {
  const profil = gestion.profils.find((p) => p.code === gestion.vuPar);
  return (
    <p className="rounded-xl bg-codialis-sky/5 px-4 py-3 text-xs text-codialis-navy">
      {profil
        ? `Aperçu : la FAQ telle qu’un profil « ${profil.libelle} » la lit, articles publiés seulement.`
        : gestion.brouillons > 0
          ? `${gestion.brouillons} brouillon${gestion.brouillons > 1 ? 's' : ''} à relire : seule la direction voit un article tant qu’il n’est pas publié.`
          : 'Tous les articles sont publiés. Chacun lit ceux qui visent son profil.'}
    </p>
  );
}

function SectionArticles({
  titre,
  articles,
  onModifier,
  onSupprimer,
}: {
  titre: string;
  articles: ArticleFaq[];
  onModifier: (article: ArticleFaq) => void;
  onSupprimer: (article: ArticleFaq) => void;
}): JSX.Element {
  return (
    <section className="overflow-hidden rounded-2xl bg-white shadow-card">
      <h2 className="font-display border-b border-gray-100 px-4 py-3.5 font-bold text-codialis-navy sm:px-6 sm:py-4">{titre}</h2>
      <ul className="divide-y divide-gray-100">
        {articles.map((article) => (
          <ArticleDepliable key={article.id} article={article} onModifier={onModifier} onSupprimer={onSupprimer} />
        ))}
      </ul>
    </section>
  );
}

function ArticleDepliable({
  article,
  onModifier,
  onSupprimer,
}: {
  article: ArticleFaq;
  onModifier: (article: ArticleFaq) => void;
  onSupprimer: (article: ArticleFaq) => void;
}): JSX.Element {
  const [ouvert, setOuvert] = useState(false);
  const idPanneau = useId();
  const toast = useToast();
  const publier = usePublierArticleFaq();
  const gestion = article.gestion;

  const basculerPublication = (): void => {
    if (!gestion) return;
    const versPublie = gestion.statut === 'BROUILLON';
    publier.mutate(
      { id: article.id, publier: versPublie },
      {
        onSuccess: () => toast(versPublie ? 'Article publié' : 'Article retiré : il repasse en brouillon'),
        onError: (erreur) => toast(messageErreur(erreur, 'Le geste n’a pas pu aboutir')),
      },
    );
  };

  return (
    <li>
      <h3>
        <button
          type="button"
          aria-expanded={ouvert}
          aria-controls={idPanneau}
          onClick={() => setOuvert((avant) => !avant)}
          className="flex min-h-[52px] w-full items-center gap-3 px-4 py-3 text-left transition hover:bg-gray-50 sm:px-6"
        >
          <span className="min-w-0 flex-1 text-sm font-semibold text-codialis-navy">{article.question}</span>
          {gestion?.statut === 'BROUILLON' && <Pastille texte="Brouillon" ton="warning" />}
          <ChevronDown
            className={`h-4 w-4 shrink-0 text-codialis-gray transition-transform ${ouvert ? 'rotate-180' : ''}`}
            aria-hidden
          />
        </button>
      </h3>
      <div id={idPanneau} hidden={!ouvert} className="space-y-3 px-4 pb-4 sm:px-6">
        <p className="whitespace-pre-line text-sm leading-relaxed text-codialis-ink">{article.reponse}</p>
        <p className="text-[11px] text-codialis-gray">
          {article.libelleRubrique} · mis à jour le {jourCourt(jourCivilReunion(article.misAJourLe))}
          {gestion && (
            <>
              {' · '}pour : {gestion.publics}
              {gestion.modifiePar ? ` · par ${gestion.modifiePar}` : ''}
            </>
          )}
        </p>
        {gestion && (
          <div className="flex flex-wrap gap-2">
            <BoutonSecondaire onClick={() => onModifier(article)}>
              <Pencil className="mr-1.5 h-4 w-4" aria-hidden />
              Modifier
            </BoutonSecondaire>
            <BoutonSecondaire onClick={basculerPublication} disabled={publier.isPending}>
              {gestion.statut === 'BROUILLON' ? (
                <>
                  <Eye className="mr-1.5 h-4 w-4" aria-hidden />
                  Publier
                </>
              ) : (
                <>
                  <EyeOff className="mr-1.5 h-4 w-4" aria-hidden />
                  Retirer de la publication
                </>
              )}
            </BoutonSecondaire>
            <BoutonSecondaire onClick={() => onSupprimer(article)}>
              <Trash2 className="mr-1.5 h-4 w-4" aria-hidden />
              Supprimer
            </BoutonSecondaire>
          </div>
        )}
      </div>
    </li>
  );
}

function ConfirmationSuppression({ article, onFermer }: { article: ArticleFaq; onFermer: () => void }): JSX.Element {
  const toast = useToast();
  const supprimer = useSupprimerArticleFaq();
  return (
    <Modale titre="Supprimer l’article" sousTitre="La suppression est définitive." ouverte onFermer={onFermer} largeur="max-w-lg">
      <div className="space-y-4 p-4 sm:p-6">
        <p className="text-sm text-codialis-navy">
          « {article.question} » ne sera plus lu par personne. Le journal d’audit garde sa question et sa réponse.
        </p>
        {supprimer.error && <MessageErreur message={messageErreur(supprimer.error, 'L’article n’a pas pu être supprimé.')} />}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <BoutonSecondaire onClick={onFermer}>Annuler</BoutonSecondaire>
          <BoutonPrincipal
            onClick={() =>
              supprimer.mutate(article.id, {
                onSuccess: () => {
                  toast('Article supprimé');
                  onFermer();
                },
              })
            }
            disabled={supprimer.isPending}
          >
            {supprimer.isPending ? 'Suppression…' : 'Supprimer'}
          </BoutonPrincipal>
        </div>
      </div>
    </Modale>
  );
}
